import { all, call, put, takeEvery } from 'redux-saga/effects';

import * as types from './constants';
import * as actions from './actions';
import { handleErrors, fetch } from '../../../api';
import api from '../../../api/endpoints';

// The endpoint answers for one collection, and a walk shares nothing with the next, so these run
// together rather than in turn. Three at a time: measured against the dev shop, that is ~2.8x a
// serial run and drains 23 of the 2000-point bucket at its deepest, which leaves the imports and
// background work that share it untouched.
const CONCURRENT_REQUESTS = 3;

const unknown = id => ({ [id]: { id, variants_count: null, is_more: false } });

// Each answer is published as it lands rather than at the end: the slowest collection must not
// hold a spinner over rows whose own number is already known. A request that fails reads as
// unknown, because a row left out would spin for ever - indistinguishable from one still on its way.
function* countOne(collectionId, signal) {
  try {
    const { data } = yield call(fetch, {
      api: api.shopify.collection_variant_count,
      params: { collection_id: collectionId },
      signal
    });

    yield put(actions.getShopifyCollectionVariantCountsDone({ counts: { [data.result.id]: data.result } }));
    return null;
  } catch (error) {
    yield put(actions.getShopifyCollectionVariantCountsDone({ counts: unknown(collectionId) }));
    return error;
  }
}

// A variant count has no aggregate behind it - the backend pages the collection to produce one -
// so these are asked for only as rows come into view, never for a whole catalogue at once.
function* getShopifyCollectionVariantCounts({ payload: { collectionIds, signal } }) {
  let failure = null;

  for (let i = 0; i < collectionIds.length; i += CONCURRENT_REQUESTS) {
    const wave = collectionIds.slice(i, i + CONCURRENT_REQUESTS);
    const errors = yield all(wave.map(id => call(countOne, id, signal)));
    failure = failure || errors.find(Boolean);
  }

  // Reported once the rows have settled, so a single failure doesn't raise one toast per collection.
  if (failure) {
    yield handleErrors({
      error: failure,
      putErrorData: actions.getShopifyCollectionVariantCountsFailed,
      endpoint: api.shopify.collection_variant_count.url
    });
  }
}

export const getShopifyCollectionVariantCountsSaga = takeEvery(
  types.GET_SHOPIFY_COLLECTION_VARIANT_COUNTS_REQUEST,
  getShopifyCollectionVariantCounts
);
